import { ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { getTenantStorageKey, useRole } from "../../contexts/RoleContext";
import NotificationBell, { NotificationItem } from "../../components/NotificationBell";
import { useTheme } from "../../contexts/ThemeContext";
import { useEntitlement } from "../../contexts/EntitlementContext";
import { authFetch, clearPersistedApiCache } from "../../utils/authFetch";
import { confirmLeaveIfGuarded } from "../../utils/leaveGuard";
import { useTenantModuleUpdates } from "../../utils/moduleUpdates";
import AppTutorial from "../../components/AppTutorial";
import { directoryEmailFor } from "../../utils/directoryIdentity";
import { ADMIN_TOUR_STEPS } from "../../components/adminTourSteps";

interface AdminLayoutProps {
  children: ReactNode;
}


type Campaign = {
  name: string;
  status: "Aktiv" | "Abgeschlossen" | "Entwurf";
  sent: number;
  clicked: number;
};

function normalizeUserEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Wie im Mitarbeiterbereich: von selbst nur für Admins, die sich ab Einführung
// der Tour zum ersten Mal angemeldet haben; sonst über das „?".
const ADMIN_TUTORIAL_AUTOSTART_SINCE = Date.parse("2026-09-25T00:00:00Z");

function adminTutorialSeenKey(tenantId: string, email: string): string {
  return `sherloxaware_admin_tutorial_seen_${tenantId}_${email}`;
}

const adminNavItems = [
  {
    path: "/admin",
    label: "Dashboard",
    end: true,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
        <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
      </svg>
    ),
  },
  {
    path: "/admin/mitarbeiter",
    label: "Mitarbeiter",
    end: true,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
      </svg>
    ),
  },
  {
    path: "/admin/mitarbeiternummern",
    label: "Mitarbeiternummern",
    end: true,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M4.5 3.75a3 3 0 00-3 3v10.5a3 3 0 003 3h15a3 3 0 003-3V6.75a3 3 0 00-3-3h-15zm4.125 3a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5zm-3.873 8.703a4.126 4.126 0 017.746 0 .75.75 0 01-.351.92 7.47 7.47 0 01-3.522.877 7.47 7.47 0 01-3.522-.877.75.75 0 01-.351-.92zM15 8.25a.75.75 0 000 1.5h3.75a.75.75 0 000-1.5H15zM14.25 12a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H15a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5h3.75a.75.75 0 000-1.5H15z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: "/admin/gruppen",
    label: "Gruppen",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" clipRule="evenodd" />
        <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
      </svg>
    ),
  },
  {
    path: "/admin/risiko",
    label: "Risikoübersicht",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 01-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 013 19.875v-6.75z" />
      </svg>
    ),
  },
  {
    path: "/admin/phishing",
    label: "Phishing",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
        <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
      </svg>
    ),
  },
  {
    path: "/admin/module-plaene",
    label: "Modul-Pläne",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h12a3 3 0 013 3v12a3 3 0 01-3 3H6a3 3 0 01-3-3V6zm4.5 7.5a.75.75 0 000 1.5h9a.75.75 0 000-1.5h-9zm0-3a.75.75 0 010-1.5h9a.75.75 0 010 1.5h-9zm0 6a.75.75 0 000 1.5h6a.75.75 0 000-1.5h-6z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: "/admin/eigene-module",
    label: "Modulbearbeitung",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M12 14l9-5-9-5-9 5 9 5z" />
        <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      </svg>
    ),
  },
  {
    path: "/admin/reports",
    label: "Berichte & Nachweise",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M2.25 13.5a8.25 8.25 0 018.25-8.25.75.75 0 01.75.75v6.75H18a.75.75 0 01.75.75 8.25 8.25 0 01-16.5 0z" clipRule="evenodd" />
        <path fillRule="evenodd" d="M12.75 3a.75.75 0 01.75-.75 8.25 8.25 0 018.25 8.25.75.75 0 01-.75.75h-7.5a.75.75 0 01-.75-.75V3z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: "/admin/benachrichtigungen",
    label: "Benachrichtigungen",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M5.85 3.5a.75.75 0 00-1.117-1 9.719 9.719 0 00-2.348 4.876.75.75 0 001.479.248A8.219 8.219 0 015.85 3.5zM19.267 2.5a.75.75 0 10-1.118 1 8.22 8.22 0 011.987 4.124.75.75 0 001.478-.248A9.72 9.72 0 0019.267 2.5z" />
        <path fillRule="evenodd" d="M12 2.25A6.75 6.75 0 005.25 9v.75a8.217 8.217 0 01-2.119 5.52.75.75 0 00.298 1.206c1.544.57 3.16.99 4.831 1.243a3.75 3.75 0 107.48 0 24.583 24.583 0 004.83-1.244.75.75 0 00.298-1.205 8.217 8.217 0 01-2.118-5.52V9A6.75 6.75 0 0012 2.25zM9.75 18c0-.034 0-.067.002-.1a25.05 25.05 0 004.496 0l.002.1a2.25 2.25 0 11-4.5 0z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: "/admin/einstellungen",
    label: "Einstellungen",
    end: false,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd" />
      </svg>
    ),
  },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [, setNotificationRefreshVersion] = useState(0);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialSidebar, setTutorialSidebar] = useState(false);
  const sidebarOpen = sidebarOpenMobile || sidebarHovered || tutorialSidebar;
  const setSidebarOpen = setSidebarOpenMobile;
  const [isMobile, setIsMobile] = useState(false);
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAdmin, isContentAdmin, tenantId } = useRole();
  const { isDark, toggleTheme } = useTheme();
  const { entitlement, hasFeature, isLoading } = useEntitlement();
  const account = instance.getActiveAccount() ?? accounts[0];
  const adminEmail = normalizeUserEmail(directoryEmailFor(account?.username));
  const canUsePhishingCampaigns = !isLoading && hasFeature("phishing_campaigns");
  const canUseAdvancedReports = !isLoading && hasFeature("advanced_reports");
  const canUseCustomModules = !isLoading && hasFeature("custom_modules");
  const canUseNotifications = !isLoading && hasFeature("notifications");
  // Angepasste Standardmodule, die SHERLOX AWARE seitdem überarbeitet hat.
  const moduleUpdates = useTenantModuleUpdates(tenantId, canUseCustomModules && (isAdmin || isContentAdmin));

  const [apiCampaigns, setApiCampaigns] = useState<Campaign[] | null>(null);
  const [apiClickStats, setApiClickStats] = useState<{ clickedUsers: number; totalClicks: number } | null>(null);
  const campaigns = apiCampaigns ?? [];
  const clickStats = apiClickStats ?? { clickedUsers: 0, totalClicks: 0 };


  useEffect(() => {
    // Phishing-Daten sind reine Admin-Sicht; der Content-Admin bekäme nur 403.
    if (!tenantId || !isAdmin) return;
    const fetchNotificationData = () => {
      authFetch(`/api/phishing-campaigns?tenantId=${encodeURIComponent(tenantId)}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setApiCampaigns(data); })
        .catch(() => {});
      authFetch(`/api/phishing-clicks?tenantId=${encodeURIComponent(tenantId)}`)
        .then(r => r.ok ? r.json() : null)
        .then((data: Record<string, number> | null) => {
          if (!data) return;
          let clickedUsers = 0;
          let totalClicks = 0;
          Object.values(data).forEach(count => {
            totalClicks += count;
            if (count > 0) clickedUsers += 1;
          });
          setApiClickStats({ clickedUsers, totalClicks });
        })
        .catch(() => {});
    };
    fetchNotificationData();
    const intervalId = setInterval(fetchNotificationData, 90 * 1000);
    const onFocus = () => fetchNotificationData();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [tenantId, isAdmin]);

  const riskyCampaigns = campaigns.filter((campaign) => {
    if (campaign.status !== "Aktiv" || campaign.sent <= 0) return false;
    const clickRate = (campaign.clicked / campaign.sent) * 100;
    return clickRate >= 20;
  });

  const notifications: NotificationItem[] = [];

  if (canUsePhishingCampaigns && riskyCampaigns.length > 0) {
    const highestRate = Math.round(Math.max(...riskyCampaigns.map((campaign) => (campaign.clicked / campaign.sent) * 100)));
    notifications.push({
      id: `admin-risky-campaign-${riskyCampaigns.length}-${highestRate}`,
      title: "Kritische aktive Kampagne",
      description: `${riskyCampaigns.length} aktive Kampagne(n) mit erhöhter Klickrate (bis ${highestRate}%).`,
      level: "warning",
      href: "/admin/phishing",
    });
  }

  if (canUsePhishingCampaigns && clickStats.clickedUsers > 0) {
    notifications.push({
      id: `admin-clicked-users-${clickStats.clickedUsers}-${clickStats.totalClicks}`,
      title: "Phishing-Klicks erkannt",
      description: `${clickStats.clickedUsers} Mitarbeiter mit Klicks (${clickStats.totalClicks} Gesamtklicks).`,
      level: "info",
      href: "/admin/phishing",
    });
  }


  if (canUseCustomModules && moduleUpdates.pending.length > 0) {
    notifications.push({
      id: `admin-module-updates-${[...moduleUpdates.pending].sort().join(",")}`,
      title: "Neue Fassung von SHERLOX AWARE",
      description: moduleUpdates.pending.length === 1
        ? "Ein Modul, das ihr angepasst habt, wurde überarbeitet. Änderungen ansehen und bei Bedarf ganz oder teilweise übernehmen."
        : `${moduleUpdates.pending.length} Module, die ihr angepasst habt, wurden überarbeitet. Änderungen ansehen und bei Bedarf ganz oder teilweise übernehmen.`,
      level: "info",
      href: "/admin/eigene-module?tab=standard",
    });
  }
  if (canUseCustomModules && moduleUpdates.mandatory.length > 0) {
    notifications.push({
      id: `admin-module-mandatory-${moduleUpdates.mandatory.map(m => `${m.moduleId}@${m.at}`).sort().join(",")}`,
      title: "Pflicht-Update von SHERLOX AWARE",
      description: moduleUpdates.mandatory.length === 1
        ? "In einem angepassten Modul wurde ein Rechts- oder Sachfehler korrigiert und in eure Fassung eingespielt. Die vorherige Fassung steht im Verlauf."
        : `In ${moduleUpdates.mandatory.length} angepassten Modulen wurden Rechts- oder Sachfehler korrigiert und in eure Fassung eingespielt.`,
      level: "warning",
      href: "/admin/eigene-module?tab=standard",
    });
  }

  const notificationPrefix = getTenantStorageKey("sherloxaware_notifications_read", tenantId);
  const legacyNotificationStorageKey = notificationPrefix ? `${notificationPrefix}_admin` : null;
  const notificationStorageKey = notificationPrefix ? `${notificationPrefix}_${adminEmail || "unknown"}` : null;

  useEffect(() => {
    const refreshNotifications = () => setNotificationRefreshVersion((value) => value + 1);

    const patchFlag = "__sherloxawareStorageEventsPatched";
    const windowWithFlags = window as unknown as Record<string, unknown>;
    if (!windowWithFlags[patchFlag]) {
      const storagePrototype = Storage.prototype;
      const originalSetItem = storagePrototype.setItem;
      const originalRemoveItem = storagePrototype.removeItem;
      const originalClear = storagePrototype.clear;

      storagePrototype.setItem = function setItemWithEvent(key: string, value: string) {
        originalSetItem.call(this, key, value);
        window.dispatchEvent(new Event("sherloxaware:storage-updated"));
      };

      storagePrototype.removeItem = function removeItemWithEvent(key: string) {
        originalRemoveItem.call(this, key);
        window.dispatchEvent(new Event("sherloxaware:storage-updated"));
      };

      storagePrototype.clear = function clearWithEvent() {
        originalClear.call(this);
        window.dispatchEvent(new Event("sherloxaware:storage-updated"));
      };

      windowWithFlags[patchFlag] = true;
    }

    window.addEventListener("sherloxaware:storage-updated", refreshNotifications);
    window.addEventListener("storage", refreshNotifications);
    return () => {
      window.removeEventListener("sherloxaware:storage-updated", refreshNotifications);
      window.removeEventListener("storage", refreshNotifications);
    };
  }, []);

  useEffect(() => {
    if (!isAdmin && !isContentAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAdmin, isContentAdmin, navigate]);

  useEffect(() => {
    if (!adminEmail || !legacyNotificationStorageKey || !notificationStorageKey) return;
    const nextValue = localStorage.getItem(notificationStorageKey);
    if (nextValue) return;

    const legacyValue = localStorage.getItem(legacyNotificationStorageKey);
    if (!legacyValue) return;

    localStorage.setItem(notificationStorageKey, legacyValue);
  }, [adminEmail, legacyNotificationStorageKey, notificationStorageKey]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpenMobile(false);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const tutorialSteps = useMemo(() => {
    const licensed: Record<string, boolean> = {
      phishing_campaigns: canUsePhishingCampaigns,
      advanced_reports: canUseAdvancedReports,
      custom_modules: canUseCustomModules,
      notifications: canUseNotifications,
    };
    return ADMIN_TOUR_STEPS.filter(s => !s.feature || licensed[s.feature] === true);
  }, [canUsePhishingCampaigns, canUseAdvancedReports, canUseCustomModules, canUseNotifications]);

  // Die Tour erklärt das Admin-Dashboard Kachel für Kachel – also dort starten.
  const openTutorial = () => {
    if (window.location.pathname !== "/admin" && confirmLeaveIfGuarded()) navigate("/admin");
    setTutorialOpen(true);
  };

  // Einführungs-Tour beim ersten Betreten des Admin-Bereichs. Erst wenn das
  // Paket geladen ist, sonst fehlen die Schritte für Phishing, Berichte usw.
  const inEditor = pathname !== "/admin" && pathname.split("/").length > 3;
  useEffect(() => {
    if (!isAdmin || !tenantId || !adminEmail || isLoading || inEditor) return;
    const seenKey = adminTutorialSeenKey(tenantId, adminEmail);
    try { if (localStorage.getItem(seenKey)) return; } catch {}
    let cancelled = false;
    authFetch(`/api/user-profiles?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(adminEmail)}`)
      .then(r => r.ok ? r.json() : null)
      .then((profile: { adminTutorialSeenAt?: string; firstLogin?: string; registeredAt?: string } | null) => {
        if (cancelled || !profile) return;
        const firstLogin = Date.parse(profile.firstLogin ?? profile.registeredAt ?? "");
        const isNewAdmin = Number.isNaN(firstLogin) || firstLogin >= ADMIN_TUTORIAL_AUTOSTART_SINCE;
        if (profile.adminTutorialSeenAt || !isNewAdmin) {
          try { localStorage.setItem(seenKey, "1"); } catch {}
          return;
        }
        openTutorial();
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, tenantId, adminEmail, isLoading, inEditor]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeTutorial = () => {
    setTutorialOpen(false);
    if (!tenantId || !adminEmail) return;
    try { localStorage.setItem(adminTutorialSeenKey(tenantId, adminEmail), "1"); } catch {}
    authFetch("/api/user-profiles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, email: adminEmail, adminTutorialSeen: true }),
    }).catch(() => {});
  };

  const forceTutorialSidebar = useCallback((open: boolean) => setTutorialSidebar(open), []);

  const handleLogout = () => {
    if (!confirmLeaveIfGuarded()) return;
    clearPersistedApiCache();
    instance.logoutRedirect();
  };

  // Kunden-Logo passend zum Theme; fehlt eine Variante, dient die andere als Fallback.
  const cobrandLogo = isDark
    ? (entitlement?.features.cobrand_logo_dark ?? entitlement?.features.cobrand_logo)
    : (entitlement?.features.cobrand_logo ?? entitlement?.features.cobrand_logo_dark);
  const cobrandOnDark = isDark && !!entitlement?.features.cobrand_logo_dark;

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-page)", overflow: "hidden", position: "relative" }}>
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150 }} />
      )}
      {/* Sidebar */}
      <aside
        id="admin-sidebar"
        onMouseEnter={() => { if (!isMobile) setSidebarHovered(true); }}
        onMouseLeave={() => setSidebarHovered(false)}
        style={{
          width: isMobile ? "240px" : (sidebarOpen ? "240px" : "64px"),
          background: isDark ? "#161B22" : "#154E8A",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflow: "hidden",
          willChange: "width",
          transition: "width 160ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 160ms ease",
          boxShadow: !isMobile && sidebarHovered ? "4px 0 16px rgba(0,0,0,0.18)" : "none",
          ...(isMobile
            ? { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 200, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)" }
            : { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 100 }),
        }}
      >
      <div style={{ width: isMobile ? "100%" : (sidebarOpen ? "240px" : "64px"), display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
        <div
          style={{
            boxSizing: "border-box",
            padding: "0 0.875rem",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              height: "56px",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              justifyContent: "flex-start",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "#fff",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              }}
            >
              <img
                src="/brand/aware-icon.png"
                alt="SHERLOX AWARE"
                width="30"
                height="30"
                style={{ display: "block", objectFit: "contain" }}
              />
            </div>
            {sidebarOpen && (
              <div style={{ minWidth: 0 }}>
                <div style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}>
                  <span style={{ fontWeight: "700", fontSize: "1rem", color: "white" }}>SHERLOX</span>
                  <span style={{ fontWeight: "400", fontSize: "1rem", color: "white" }}> AWARE</span>
                </div>
                <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.65)", marginTop: "2px", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
                  Administration
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav data-tour="nav" style={{ padding: "0.75rem 0.5rem", flex: 1, overflowY: "auto", minHeight: 0 }}>
          {/* Content-Admins sehen nur die Modulbearbeitung. */}
          {adminNavItems.filter(item => isAdmin || item.path === "/admin/eigene-module").map((item) => {
            const lockedByPlan =
              (item.path === "/admin/phishing" && !canUsePhishingCampaigns)
              || (item.path === "/admin/reports" && !canUseAdvancedReports)
              || (item.path === "/admin/eigene-module" && !canUseCustomModules)
              || (item.path === "/admin/benachrichtigungen" && !canUseNotifications);

            if (lockedByPlan) {
              return (
                <div
                  key={item.path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: sidebarOpen ? "0.625rem" : "0",
                    padding: "0.6rem 0.75rem",
                    borderRadius: "6px",
                    marginBottom: "2px",
                    color: "var(--text-muted)",
                    fontSize: "0.875rem",
                    fontWeight: "400",
                    cursor: "default",
                    justifyContent: "flex-start",
                  }}
                >
                  {item.icon}
                  {sidebarOpen && (
                    <>
                      <span>{item.label}</span>
                      <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", marginLeft: "auto" }}>Nicht im Plan</span>
                    </>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                data-tour={`nav-${item.path.split("/").pop()}`}
                onClick={(e) => { if (!confirmLeaveIfGuarded()) { e.preventDefault(); return; } if (isMobile) setSidebarOpen(false); }}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: sidebarOpen ? "0.625rem" : "0",
                  padding: "0.6rem 0.75rem",
                  borderRadius: "6px",
                  marginBottom: "2px",
                  color: isDark ? (isActive ? "#F97316" : "rgba(240,246,252,0.75)") : "white",
                  background: isActive ? (isDark ? "rgba(249,115,22,0.15)" : "#1559A0") : "transparent",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: isActive ? "600" : "400",
                  transition: "background 0.15s ease",
                  justifyContent: "flex-start",
                })}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (el.getAttribute("aria-current") !== "page") {
                    el.style.background = "rgba(255,255,255,0.12)";
                  }
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (el.getAttribute("aria-current") !== "page") {
                    el.style.background = "transparent";
                  }
                }}
              >
                {item.icon}
                {sidebarOpen && item.label}
                {item.path === "/admin/eigene-module" && moduleUpdates.pending.length > 0 && (
                  <span
                    title="Neue Fassung von SHERLOX AWARE"
                    style={{
                      marginLeft: sidebarOpen ? "auto" : "-0.35rem", alignSelf: sidebarOpen ? "center" : "flex-start",
                      minWidth: "1.1rem", height: "1.1rem", padding: "0 0.3rem", borderRadius: "999px",
                      background: "#F97316", color: "#FFFFFF", fontSize: "0.65rem", fontWeight: 800,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {moduleUpdates.pending.length}
                  </span>
                )}
              </NavLink>
            );
          })}

          <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
            <NavLink
              to="/dashboard"
              data-tour="nav-employee"
              onClick={(e) => { if (!confirmLeaveIfGuarded()) { e.preventDefault(); return; } if (isMobile) setSidebarOpen(false); }}
              style={() => ({
                display: "flex",
                alignItems: "center",
                gap: sidebarOpen ? "0.625rem" : "0",
                padding: "0.6rem 0.75rem",
                borderRadius: "6px",
                color: "rgba(255,255,255,0.75)",
                background: "transparent",
                textDecoration: "none",
                fontSize: "0.8rem",
                fontWeight: "400",
                justifyContent: "flex-start",
              })}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
              </svg>
              {sidebarOpen && "Mitarbeiter-Ansicht"}
            </NavLink>
          </div>
        </nav>

        {/* Cobrand-Logo unter der Nav statt im Header: die Nav startet dadurch
            in beiden Sidebar-Zuständen an derselben Y-Position und springt nicht. */}
        {sidebarOpen && cobrandLogo && (
          <div style={{ flexShrink: 0, padding: "0.625rem 0.875rem", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ background: cobrandOnDark ? "rgba(255,255,255,0.08)" : "#fff", borderRadius: "6px", padding: "5px 8px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
              <img src={cobrandLogo} alt={entitlement?.features.cobrand_name ?? "Logo"} style={{ maxHeight: "30px", maxWidth: "100%", objectFit: "contain", display: "block" }} />
            </div>
          </div>
        )}

        {/* User info at bottom */}
        <div
          style={{
            padding: "0.875rem 0.5rem",
            borderTop: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.15)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", justifyContent: "flex-start" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.875rem",
                fontWeight: "700",
                color: "white",
                flexShrink: 0,
              }}
            >
              {account?.name?.charAt(0)?.toUpperCase() ?? "A"}
            </div>
            {sidebarOpen && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <div style={{ fontSize: "0.8rem", color: "white", fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {account?.name ?? "Admin"}
                  </div>
                  <span style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "4px", fontSize: "0.6rem", fontWeight: "700", padding: "1px 4px", flexShrink: 0 }}>
                    ADMIN
                  </span>
                </div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {account?.username ?? ""}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </aside>

      {/* Main area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, height: "100vh", overflow: "hidden", marginLeft: isMobile ? 0 : "64px" }}>
        {/* Top navbar */}
        <header
          id="admin-topbar"
          style={{
            height: "56px",
            background: "var(--bg-card)",
            borderBottom: "1px solid var(--border)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 1rem",
            gap: "0.75rem",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-secondary)", cursor: "pointer", flexShrink: 0, marginRight: "0.25rem" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M3 6.75A.75.75 0 013.75 6h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 6.75zM3 12a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm0 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75a.75.75 0 01-.75-.75z" clipRule="evenodd" /></svg>
              </button>
            )}
            {!isMobile && (
              <>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#1B6AB1" }} />
                <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontWeight: "500" }}>{isAdmin ? "Admin-Modus aktiv" : "Content-Admin: Modulbearbeitung"}</span>
              </>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {/* Einführungs-Tour – erklärt das Admin-Dashboard, also nur für Admins. */}
            {isAdmin && (
            <button
              data-tour="help"
              onClick={openTutorial}
              title="Einführung & Hilfe"
              aria-label="Einführung öffnen"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--text-secondary)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                flexShrink: 0,
                fontSize: "0.95rem",
                fontWeight: 700,
                fontFamily: "inherit",
              }}
              onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-card-alt)"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              ?
            </button>
            )}

            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? "Hellmodus aktivieren" : "Dunkelmodus aktivieren"}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--text-secondary)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                flexShrink: 0,
              }}
              onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-card-alt)"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              {isDark ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.166 17.834a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.166 6.166a.75.75 0 001.06 1.06l1.591-1.59a.75.75 0 10-1.06-1.061l-1.591 1.59z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <div data-tour="notifications" style={{ display: "flex" }}>
              <NotificationBell
                notifications={notifications}
                storageKey={notificationStorageKey ?? `sherloxaware_notifications_read_admin_unavailable_${adminEmail || "unknown"}`}
                emptyText="Aktuell keine administrativen Hinweise."
                onNavigate={(href) => { if (!confirmLeaveIfGuarded()) return; navigate(href); }}
              />
            </div>
            <button
              onClick={handleLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.375rem 0.875rem",
                background: "transparent",
                border: "1px solid #1B6AB1",
                borderRadius: "6px",
                color: "#1B6AB1",
                fontSize: "0.8rem",
                cursor: "pointer",
                fontWeight: "500",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "#1B6AB1";
                (e.currentTarget as HTMLButtonElement).style.color = "#ffffff";
              }}
              onMouseOut={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                (e.currentTarget as HTMLButtonElement).style.color = "#1B6AB1";
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
              Abmelden
            </button>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? "1rem" : "1.5rem", overflow: "auto", background: "var(--bg-page)" }}>
          <Suspense fallback={
            <div style={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ width: "32px", height: "32px", border: "3px solid var(--border)", borderTop: "3px solid #1B6AB1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <span>Lädt…</span>
              </div>
            </div>
          }>
            {children}
          </Suspense>
        </main>
      </div>

      <AppTutorial
        open={tutorialOpen}
        language="de"
        steps={tutorialSteps}
        finishLabel={{ de: "Zu den Modul-Plänen" }}
        onClose={closeTutorial}
        onFinish={() => {
          closeTutorial();
          if (confirmLeaveIfGuarded()) navigate("/admin/module-plaene");
        }}
        onSidebarForce={forceTutorialSidebar}
      />
    </div>
  );
}
